import axios from 'axios';

const BASE_URL = '';

let onTokenRefreshFail = null;
let impersonateRole = null;

// Cookies are sent automatically on same-origin requests; `withCredentials`
// is needed so axios attaches them on cross-origin too (e.g. preview sub-domains).
// Tokens now live in httpOnly cookies — no localStorage, no Bearer header.
const api = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  withCredentials: true,
});

// Legacy stubs — kept so callers in transition don't break compile.
// Cookie storage is server-managed; these are no-ops now.
export const setTokens = (_access, _refresh) => { /* httpOnly cookies — server-managed */ };
export const clearTokens = () => { /* server clears via /auth/logout */ };
export const getAccessToken = () => null;

export const setOnRefreshFail = (fn) => { onTokenRefreshFail = fn; };

export const setImpersonateRole = (role) => { impersonateRole = role; };

// Request interceptor — only attach impersonation header now
api.interceptors.request.use((config) => {
  if (impersonateRole) {
    config.headers['X-Impersonate-Role'] = impersonateRole;
  }
  return config;
});

// Response interceptor — handle 401 by calling /auth/refresh (cookie-driven)
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url = original?.url || '';
    // Pre-auth endpoints can legitimately return 401 (wrong OTP, bad password,
    // missing cookie). Don't try to refresh on those — the refresh would itself
    // 401 with "Missing refresh token" and mask the real error.
    const isAuthEndpoint = url.includes('/auth/login')
      || url.includes('/auth/send-otp')
      || url.includes('/auth/verify-otp')
      || url.includes('/auth/trust-login')
      || url.includes('/auth/refresh');
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !isAuthEndpoint
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(original));
      }
      original._retry = true;
      isRefreshing = true;
      try {
        // Server reads refresh_token from httpOnly cookie and rotates both
        await axios.post(`${BASE_URL}/api/v1/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return api(original);
      } catch (err) {
        processQueue(err);
        if (onTokenRefreshFail) onTokenRefreshFail();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
